/** React hook for Web MIDI input — handles device connection, note routing,
 * sustain pedal, pitch bend, CC mapping, and MIDI learn mode.
 *
 * All MIDI event handlers use refs so the `connect` callback is fully stable
 * and the auto-connect effect never re-fires due to handler identity changes.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type {
	MidiCCMessage,
	MidiInputConnection,
	MidiInputDevice,
	MidiNoteMessage,
	MidiPitchBendMessage,
} from "../lib/midiInput.ts";
import {
	ccToLogFrequency,
	ccToNormalized,
	ccToRange,
	connectMidiInputs,
	isMidiAvailable,
} from "../lib/midiInput.ts";
import type {
	MappableParam,
	MidiActivity,
	MidiMappingState,
} from "../lib/midiMapping.ts";
import {
	cancelLearning,
	createInitialMappingState,
	learnCC,
	removeMapping,
	resolveCC,
	startLearning,
	updateActivity,
} from "../lib/midiMapping.ts";
import type { ADSRParams, FilterParams } from "../types/synth.ts";

/** Options for the useMidiInput hook. */
export type UseMidiInputOptions = {
	readonly noteOn: (note: number, channel: number) => void;
	readonly noteOff: (note: number, channel: number) => void;
	readonly setFilter: (filter: Partial<FilterParams>) => void;
	readonly setADSR: (adsr: Partial<ADSRParams>) => void;
	readonly setMasterGain: (gain: number) => void;
	readonly enabled: boolean;
};

/** Return value of the useMidiInput hook. */
export type UseMidiInputResult = {
	readonly available: boolean;
	readonly connected: boolean;
	readonly devices: readonly MidiInputDevice[];
	readonly sustainPedalDown: boolean;
	readonly pitchBend: number;
	readonly lastNoteChannel: number | null;
	readonly lastActivity: MidiActivity | null;
	readonly mappingState: MidiMappingState;
	readonly startLearn: (param: MappableParam) => void;
	readonly cancelLearn: () => void;
	readonly removeMappingForParam: (param: MappableParam) => void;
	readonly connect: () => Promise<void>;
};

/** Apply a CC value to the mapped parameter via the latest option refs. */
function applyCCToParam(
	param: MappableParam,
	value: number,
	opts: {
		readonly setFilter: (filter: Partial<FilterParams>) => void;
		readonly setADSR: (adsr: Partial<ADSRParams>) => void;
		readonly setMasterGain: (gain: number) => void;
	},
): void {
	const norm = ccToNormalized(value);

	switch (param) {
		case "masterGain":
			opts.setMasterGain(norm);
			break;
		case "filter.cutoff":
			opts.setFilter({ cutoff: ccToLogFrequency(value) });
			break;
		case "filter.resonance":
			opts.setFilter({ resonance: ccToRange(value, 0.1, 30) });
			break;
		case "adsr.attack":
			opts.setADSR({ attack: ccToRange(value, 0.001, 5) });
			break;
		case "adsr.decay":
			opts.setADSR({ decay: ccToRange(value, 0.001, 5) });
			break;
		case "adsr.sustain":
			opts.setADSR({ sustain: norm });
			break;
		case "adsr.release":
			opts.setADSR({ release: ccToRange(value, 0.001, 10) });
			break;
	}
}

export function useMidiInput(options: UseMidiInputOptions): UseMidiInputResult {
	const { noteOn, noteOff, setFilter, setADSR, setMasterGain, enabled } =
		options;

	const [available] = useState(() => isMidiAvailable());
	const [connected, setConnected] = useState(false);
	const [devices, setDevices] = useState<readonly MidiInputDevice[]>([]);
	const [sustainPedalDown, setSustainPedalDown] = useState(false);
	const [pitchBend, setPitchBend] = useState(0);
	const [lastNoteChannel, setLastNoteChannel] = useState<number | null>(null);
	const [mappingState, setMappingState] = useState<MidiMappingState>(
		createInitialMappingState,
	);

	const connectionRef = useRef<MidiInputConnection | null>(null);
	const connectingRef = useRef(false);
	const sustainedNotesRef = useRef<Map<number, number>>(new Map());
	const sustainPedalRef = useRef(sustainPedalDown);

	// ---- Keep refs in sync so MIDI callbacks always see the latest values ----
	const enabledRef = useRef(enabled);
	enabledRef.current = enabled;

	const noteOnRef = useRef(noteOn);
	noteOnRef.current = noteOn;

	const noteOffRef = useRef(noteOff);
	noteOffRef.current = noteOff;

	const setFilterRef = useRef(setFilter);
	setFilterRef.current = setFilter;

	const setADSRRef = useRef(setADSR);
	setADSRRef.current = setADSR;

	const setMasterGainRef = useRef(setMasterGain);
	setMasterGainRef.current = setMasterGain;

	const mappingStateRef = useRef(mappingState);
	mappingStateRef.current = mappingState;

	sustainPedalRef.current = sustainPedalDown;

	// ---- Stable connect function — no handler deps, uses refs throughout ----
	const connect = useCallback(async () => {
		if (connectionRef.current || connectingRef.current) return;
		if (!available) return;

		connectingRef.current = true;

		try {
			const conn = await connectMidiInputs({
				onNoteOn(msg: MidiNoteMessage) {
					if (!enabledRef.current) return;
					setLastNoteChannel(msg.channel);
					noteOnRef.current(msg.note, msg.channel);
				},
				onNoteOff(msg: MidiNoteMessage) {
					if (!enabledRef.current) return;
					if (sustainPedalRef.current) {
						sustainedNotesRef.current.set(msg.note, msg.channel);
					} else {
						noteOffRef.current(msg.note, msg.channel);
					}
				},
				onCC(msg: MidiCCMessage) {
					const state = mappingStateRef.current;

					// Update activity feedback
					setMappingState((prev) => updateActivity(prev, msg));

					// If in learn mode, bind the CC
					if (state.isLearning && state.learningParam) {
						setMappingState((prev) => learnCC(prev, msg));
						return;
					}

					// Otherwise route to mapped parameter
					const param = resolveCC(state, msg);
					if (param) {
						applyCCToParam(param, msg.value, {
							setFilter: setFilterRef.current,
							setADSR: setADSRRef.current,
							setMasterGain: setMasterGainRef.current,
						});
					}
				},
				onPitchBend(msg: MidiPitchBendMessage) {
					setPitchBend(msg.value);
				},
				onSustainPedal(pressed: boolean) {
					setSustainPedalDown(pressed);
					if (!pressed) {
						for (const [note, channel] of sustainedNotesRef.current) {
							noteOffRef.current(note, channel);
						}
						sustainedNotesRef.current.clear();
					}
				},
				onDeviceChange(newDevices: readonly MidiInputDevice[]) {
					setDevices(newDevices);
				},
			});

			connectionRef.current = conn;
			setConnected(true);
		} finally {
			connectingRef.current = false;
		}
	}, [available]); // only depends on the immutable `available` flag

	// Auto-connect when enabled
	useEffect(() => {
		if (enabled && available && !connected) {
			connect();
		}
	}, [enabled, available, connected, connect]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			connectionRef.current?.disconnect();
			connectionRef.current = null;
		};
	}, []);

	const startLearn = useCallback((param: MappableParam) => {
		setMappingState((prev) => startLearning(prev, param));
	}, []);

	const cancelLearn = useCallback(() => {
		setMappingState((prev) => cancelLearning(prev));
	}, []);

	const removeMappingForParam = useCallback((param: MappableParam) => {
		setMappingState((prev) => removeMapping(prev, param));
	}, []);

	return {
		available,
		connected,
		devices,
		sustainPedalDown,
		pitchBend,
		lastNoteChannel,
		lastActivity: mappingState.lastActivity,
		mappingState,
		startLearn,
		cancelLearn,
		removeMappingForParam,
		connect,
	};
}
